import { getR2PublicUrl } from '../services/r2Service';

/**
 * Get the public URL for a single image from R2.
 * Assumes the R2 bucket is public for reads.
 * @param {string} objectKey - The R2 object key.
 * @returns {string} The public URL.
 */
export const getImageDownloadURL = (objectKey) => {
  // The R2 bucket is assumed to be public, so we can construct the URL directly.
  // No async call needed here if getR2PublicUrl is synchronous.
  if (!objectKey) {
    console.warn('getImageDownloadURL called with no objectKey');
    return '';
  }
  try {
    return getR2PublicUrl(objectKey);
  } catch (error) {
    // This might happen if R2_PUBLIC_BASE_URL is not configured, though getR2PublicUrl should handle it.
    console.error('Error constructing R2 public URL:', error);
    // Depending on desired behavior, could return a placeholder or re-throw.
    // For now, returning an empty string or a specific error indicator might be best.
    return ''; // Or throw new Error('Failed to construct R2 public URL');
  }
};

/**
 * Download an image as a blob using its URL.
 * For R2, if the bucket is public, this can be a direct fetch.
 * If the Cloud Function proxy was for Firebase's signed URLs or auth,
 * it might still be needed if R2 objects are not publicly readable directly
 * or if some transformation/logging is done by the proxy.
 * Assuming public R2 for now.
 * @param {string} url - The public download URL of the image from R2.
 * @returns {Promise<Blob>} The image blob
 */
export const downloadImageAsBlob = async (url) => {
  try {
    // If R2 bucket is public, direct fetch should work.
    // If VITE_FIREBASE_FUNCTIONS_URL/downloadMedia was specifically for Firebase token/CORS,
    // and R2 is public, this proxy might not be needed.
    // If R2 is private and requires signed URLs for download, a new worker endpoint would be needed.
    // For now, assuming public R2 and direct fetch.
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorText = await response.text();
      // Consider more specific error handling based on response.status
      throw new Error(`Network response was not ok fetching from R2: ${response.status} ${errorText}`);
    }

    const blob = await response.blob();
    if (blob.size === 0) {
      throw new Error('Received empty blob');
    }

    return blob;
  } catch (error) {
    console.error('Error downloading image:', error);
    throw new Error('Failed to download image: ' + error.message);
  }
};

/**
 * Download multiple images with progress tracking
 * @param {Array<string>} objectKeys - Array of R2 object keys.
 * @param {Function} onProgress - Callback function for progress updates
 * @returns {Promise<Array<{key: string, blob: Blob, error?: Error}>>} Array of download results
 */
export const batchDownloadImages = async (objectKeys, onProgress) => {
  const results = [];
  const total = objectKeys.length;
  let completed = 0;

  const updateProgress = () => {
    completed++;
    if (onProgress) {
      onProgress({ completed, total });
    }
  };

  // Process downloads in parallel but with a concurrency limit
  const BATCH_SIZE = 5; // Process 5 downloads at a time
  for (let i = 0; i < objectKeys.length; i += BATCH_SIZE) {
    const batch = objectKeys.slice(i, i + BATCH_SIZE);
    const batchPromises = batch.map(async (key) => {
      try {
        const url = getImageDownloadURL(key); // This is now synchronous
        if (!url) {
          throw new Error(`Could not get URL for R2 object key: ${key}`);
        }
        const blob = await downloadImageAsBlob(url);
        updateProgress();
        return { key, blob };
      } catch (error) {
        updateProgress();
        return { key, error };
      }
    });

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }

  return results;
};

/**
 * Create a blob URL for an image
 * @param {Blob} blob - The image blob
 * @returns {string} The blob URL
 */
export const createBlobURL = (blob) => {
  return URL.createObjectURL(blob);
};

/**
 * Revoke a blob URL to free up memory
 * @param {string} url - The blob URL to revoke
 */
export const revokeBlobURL = (url) => {
  URL.revokeObjectURL(url);
};
